import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  FileJson,
  Loader2,
  Search,
  Upload,
  User as UserIcon,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  adminListPlayerProfiles,
  adminListClubsCatalog,
  adminListTeamsCatalog,
  adminImportPlayerMatch,
  buildAssetUrl,
  friendlyError,
} from '@/lib/api';
import { resolvePlayerAvatar } from '@/lib/playerAvatar';
import { normalizePlayerMatch, selectKpisForPlayerMatch } from '@/lib/playerIntelligence';
import { ROUTES } from '@/lib/constants';

/**
 * Fase 3B — Importador Admin.
 *
 * Flujo:
 *   1) Seleccionar `player_profile` NetScouters existente (searchbox).
 *   2) Subir JSON RAW BUILDUP (1 jugador · 1 partido).
 *   3) Ejecutar `normalizePlayerMatch()` EXISTENTE en el frontend.
 *   4) Mostrar preview mínimo (identidad + metadatos + 4 KPIs).
 *   5) Bloquear si la identidad discrepa razonablemente.
 *   6) POST /api/admin/player-intelligence/player-matches
 *      (persiste `raw_json` + `normalized_data` sin `source_raw`).
 *
 * NO se duplica el normalizador; se reutiliza el mismo módulo consumido
 * hoy por la UI. NO se implementa Evolution, IA, PDF, vídeo ni
 * importación masiva.
 */

// ---------------------------------------------------------------------------
// Helpers de identidad (comparación razonable, sin fuzzy sofisticado)
// ---------------------------------------------------------------------------
function _normalizeText(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
function _profileFullName(p) {
  if (!p) return '';
  const parts = [
    p.first_name,
    p.first_surname || (p.last_name ? p.last_name.split(' ')[0] : ''),
    p.second_surname || (p.last_name ? p.last_name.split(' ').slice(1).join(' ') : ''),
  ].filter(Boolean);
  return parts.join(' ').trim();
}
function _rawJsonPlayerNameParts(raw) {
  const p = raw?.player || {};
  // Preferimos campos estructurados; caemos a `name`/`full_name` cuando el
  // proveedor sólo entrega un string plano (ej. "Mario  Callealta").
  const first = p.first_name || null;
  const last = p.last_name || p.second_surname
    ? [p.last_name, p.second_surname].filter(Boolean).join(' ').trim()
    : null;
  if (first || last) return { first, last, full: [first, last].filter(Boolean).join(' ').trim() };
  const flat = String(p.name || p.full_name || '').replace(/\s+/g, ' ').trim();
  if (!flat) return { first: null, last: null, full: null };
  const parts = flat.split(' ');
  return {
    first: parts[0] || null,
    last: parts.slice(1).join(' ') || null,
    full: flat,
  };
}

function _rawJsonPlayerName(raw) {
  return _rawJsonPlayerNameParts(raw).full;
}

function _compact(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function _identityMatch(profile, raw) {
  // Regla mínima "razonable" (sin fuzzy sofisticado):
  //   - primer nombre debe coincidir tras normalizar,
  //   - la forma compacta de los apellidos (perfil vs JSON) debe
  //     coincidir por inclusión bidireccional o compartir un prefijo
  //     largo. Esto acepta "Mario Callealta" ↔ "Mario Calle Alta"
  //     y rechaza jugadores claramente distintos.
  const parts = _rawJsonPlayerNameParts(raw);
  const pFn = _normalizeText(profile?.first_name);
  const rFn = _normalizeText(parts.first);
  if (!pFn || !rFn) return { ok: false, reason: 'unknown' };
  if (pFn !== rFn) return { ok: false, reason: 'first_name_mismatch' };

  const pSurCompact = _compact([
    profile?.first_surname,
    profile?.second_surname,
    (!profile?.first_surname ? profile?.last_name : ''),
  ].filter(Boolean).join(' '));
  const rSurCompact = _compact(parts.last || '');
  if (!pSurCompact || !rSurCompact) {
    // Sin apellidos comparables: consideramos ok si al menos el nombre
    // coincide y no hay evidencia contraria.
    return { ok: true };
  }
  if (pSurCompact === rSurCompact) return { ok: true };
  if (pSurCompact.includes(rSurCompact)) return { ok: true };
  if (rSurCompact.includes(pSurCompact)) return { ok: true };
  const prefixLen = Math.min(pSurCompact.length, rSurCompact.length);
  if (prefixLen >= 5) {
    // Prefijo largo compartido → mismo apellido con diferente separación.
    let i = 0;
    while (i < prefixLen && pSurCompact[i] === rSurCompact[i]) i += 1;
    if (i >= Math.max(5, Math.floor(prefixLen * 0.6))) return { ok: true };
  }
  return { ok: false, reason: 'surname_mismatch' };
}

// ---------------------------------------------------------------------------
// Player picker — Club → Equipo → Buscador + Tabla + Tarjeta seleccionado
// ---------------------------------------------------------------------------
function _profileShortId(p) {
  const id = String(p?.id || '');
  return id ? id.slice(-6) : '—';
}
function _profileDorsal(p) {
  return p?.shirt_number || p?.jersey_number || p?.number || null;
}

function PlayerPicker({ selected, onSelect }) {
  // Catálogos globales (una sola carga por sesión de página).
  const [clubs, setClubs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  const [clubId, setClubId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, t] = await Promise.all([
          adminListClubsCatalog().catch(() => ({ items: [] })),
          adminListTeamsCatalog().catch(() => ({ items: [] })),
        ]);
        if (!alive) return;
        const cItems = Array.isArray(c) ? c : (c?.items || []);
        const tItems = Array.isArray(t) ? t : (t?.items || []);
        setClubs(cItems.filter((x) => x?.status !== 'disabled'));
        setTeams(tItems.filter((x) => x?.status !== 'disabled'));
      } catch (err) {
        toast.error(friendlyError(err) || 'No se pudieron cargar los catálogos.');
      } finally {
        if (alive) setCatalogLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Diccionarios para resolver nombres rápidamente.
  const clubById = useMemo(() => {
    const m = {};
    clubs.forEach((c) => { m[c.id] = c; });
    return m;
  }, [clubs]);
  const teamById = useMemo(() => {
    const m = {};
    teams.forEach((t) => { m[t.id] = t; });
    return m;
  }, [teams]);

  const teamsForClub = useMemo(
    () => (clubId ? teams.filter((t) => t.club_id === clubId) : []),
    [teams, clubId],
  );

  // Al cambiar de club, si el equipo actual ya no pertenece → resetea.
  useEffect(() => {
    if (!teamId) return;
    const t = teamById[teamId];
    if (!t || (clubId && t.club_id !== clubId)) setTeamId('');
  }, [clubId, teamId, teamById]);

  // Si el jugador seleccionado deja de corresponder al club/equipo elegido → limpiar.
  useEffect(() => {
    if (!selected) return;
    if (clubId && selected.club_id && selected.club_id !== clubId) {
      onSelect(null);
      return;
    }
    if (teamId && selected.team_id && selected.team_id !== teamId) {
      onSelect(null);
    }
  }, [clubId, teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Búsqueda: si hay query ≥ 2 chars, llama al endpoint admin. Filtra
  // en cliente por club/team seleccionados. Sin filtros ni query,
  // no se muestran resultados (para no volcar toda la BBDD).
  useEffect(() => {
    let alive = true;
    const q = query.trim();
    const hasFilter = !!(clubId || teamId);
    if (q.length < 2 && !hasFilter) { setItems([]); return undefined; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await adminListPlayerProfiles({
          query: q || undefined,
          page: 1,
          page_size: 50,
        });
        if (!alive) return;
        let arr = res?.items || [];
        if (clubId) arr = arr.filter((it) => it.club_id === clubId);
        if (teamId) arr = arr.filter((it) => it.team_id === teamId);
        setItems(arr.slice(0, 25));
      } catch (err) {
        if (alive) toast.error(friendlyError(err) || 'No se pudo buscar el jugador.');
      } finally {
        if (alive) setSearching(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [query, clubId, teamId]);

  const renderTeamLabel = (t) => {
    const category = t?.category || null;
    return category ? `${t.name} · ${category}` : (t?.name || 'Sin nombre');
  };

  const helperText = (() => {
    if (searching) return 'Buscando…';
    if (query.trim().length < 2 && !(clubId || teamId)) return 'Escribe al menos 2 caracteres o elige un club.';
    if (items.length === 0) return 'Ningún jugador coincide con los filtros.';
    return null;
  })();

  return (
    <Card data-testid="admin-pi-import-player-picker">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserIcon className="h-5 w-5" /> 1 · Selecciona el jugador NetScouters
        </CardTitle>
        <CardDescription>
          Filtra por club y equipo, y luego busca y selecciona el jugador.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Fila de filtros */}
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Club</Label>
            <Select
              value={clubId || 'all'}
              onValueChange={(v) => setClubId(v === 'all' ? '' : v)}
              disabled={catalogLoading}
            >
              <SelectTrigger data-testid="admin-pi-import-club-select">
                <SelectValue placeholder="Todos los clubes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los clubes</SelectItem>
                {clubs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Equipo / Categoría</Label>
            <Select
              value={teamId || 'all'}
              onValueChange={(v) => setTeamId(v === 'all' ? '' : v)}
              disabled={!clubId || teamsForClub.length === 0}
            >
              <SelectTrigger data-testid="admin-pi-import-team-select">
                <SelectValue placeholder={clubId ? 'Todos los equipos' : 'Selecciona club primero'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los equipos</SelectItem>
                {teamsForClub.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{renderTeamLabel(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Buscar jugador</Label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                data-testid="admin-pi-import-player-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nombre o apellido"
                className="pl-9 pr-8"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Limpiar búsqueda"
                  className="absolute right-2 top-2 rounded p-0.5 text-muted-foreground hover:text-foreground"
                  data-testid="admin-pi-import-player-search-clear"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabla de resultados */}
        <div className="rounded-lg border overflow-hidden">
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_80px_100px_100px] items-center gap-2 bg-muted/40 px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground">
            <div>Jugador</div>
            <div className="hidden md:block">Equipo</div>
            <div>Dorsal</div>
            <div>Posición</div>
            <div>ID (últ. 6)</div>
          </div>
          <div className="max-h-72 overflow-auto divide-y">
            {catalogLoading && (
              <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando catálogos…
              </div>
            )}
            {!catalogLoading && helperText && items.length === 0 && (
              <div className="p-3 text-sm text-muted-foreground" data-testid="admin-pi-import-player-empty">
                {helperText}
              </div>
            )}
            {items.map((it) => {
              const isSel = selected?.id === it.id;
              const avatar = resolvePlayerAvatar(
                { profile_photo: it.profile_photo, gender: it.gender },
                { size: 96 },
              );
              const avatarUrl = avatar && avatar.startsWith('http') ? avatar : buildAssetUrl(avatar);
              const clubName = clubById[it.club_id]?.name || '—';
              const team = teamById[it.team_id];
              const teamLabel = team ? renderTeamLabel(team) : '—';
              const dorsal = _profileDorsal(it);
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onSelect(it)}
                  data-testid={`admin-pi-import-player-option-${it.id}`}
                  className={`w-full grid grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_80px_100px_100px] items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/60 transition-colors ${isSel ? 'bg-primary/5' : ''}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <input
                      type="radio"
                      checked={isSel}
                      readOnly
                      aria-label="Seleccionar"
                      className="accent-primary"
                    />
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt=""
                        className="h-8 w-8 rounded-full object-cover border"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-muted border" />
                    )}
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {[it.first_name, it.first_surname || it.last_name, it.second_surname].filter(Boolean).join(' ') || '(sin nombre)'}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{clubName}</div>
                    </div>
                  </div>
                  <div className="hidden md:block truncate">{teamLabel}</div>
                  <div className="tabular-nums">{dorsal ? `#${dorsal}` : '—'}</div>
                  <div className="truncate">{it.position || '—'}</div>
                  <div className="font-mono text-xs">{_profileShortId(it)}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tarjeta Jugador Seleccionado */}
        {selected && (
          <SelectedPlayerCard
            profile={selected}
            club={clubById[selected.club_id]}
            team={teamById[selected.team_id]}
            onClear={() => onSelect(null)}
          />
        )}
      </CardContent>
    </Card>
  );
}

function SelectedPlayerCard({ profile, club, team, onClear }) {
  const avatar = resolvePlayerAvatar(
    { profile_photo: profile.profile_photo, gender: profile.gender },
    { size: 160 },
  );
  const avatarUrl = avatar && avatar.startsWith('http') ? avatar : buildAssetUrl(avatar);
  const fullName = [profile.first_name, profile.first_surname || profile.last_name, profile.second_surname].filter(Boolean).join(' ') || '(sin nombre)';
  const clubName = club?.name || '—';
  const teamLabel = team ? (team.category ? `${team.name} · ${team.category}` : team.name) : '—';
  const dorsal = _profileDorsal(profile);
  const shortId = _profileShortId(profile);
  return (
    <div
      className="rounded-lg border bg-primary/5 p-4 flex flex-wrap md:flex-nowrap items-center gap-4"
      data-testid="admin-pi-import-selected-player"
    >
      <div className="shrink-0">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="h-16 w-16 rounded-full object-cover border" />
        ) : (
          <div className="h-16 w-16 rounded-full bg-muted border" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-primary font-semibold">
          Jugador seleccionado
        </div>
        <div className="text-lg font-semibold truncate" data-testid="admin-pi-import-selected-player-name">
          {fullName}
        </div>
        <div className="text-sm text-muted-foreground truncate">
          {clubName} · {teamLabel}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 shrink-0">
        <div className="text-center min-w-[64px]">
          <div className="text-lg font-semibold tabular-nums">{dorsal ? `#${dorsal}` : '—'}</div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Dorsal</div>
        </div>
        <div className="text-center min-w-[64px]">
          <div className="text-lg font-semibold">{profile.position || '—'}</div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Posición</div>
        </div>
        <div className="text-center min-w-[80px]">
          <div className="text-lg font-mono">{shortId}</div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">ID (últ. 6)</div>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClear}
        data-testid="admin-pi-import-clear-selected"
      >
        Cambiar
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// JSON uploader
// ---------------------------------------------------------------------------
function JsonUploader({ raw, onLoad, disabled }) {
  const [error, setError] = useState(null);
  const inputRef = React.useRef(null);
  const [fileName, setFileName] = useState(null);
  const [pasted, setPasted] = useState('');

  const handleFile = async (file) => {
    setError(null);
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      setError('Sólo se admite un archivo `.json`.');
      return;
    }
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setError('El JSON debe ser un objeto (1 jugador · 1 partido).');
        return;
      }
      setFileName(file.name);
      setPasted('');
      onLoad(parsed);
    } catch (e) {
      setError('JSON inválido: revisa el formato.');
      onLoad(null);
    }
  };

  const handlePasted = () => {
    setError(null);
    const text = String(pasted || '').trim();
    if (!text) {
      setError('Pega el JSON antes de analizarlo.');
      onLoad(null);
      return;
    }
    let parsed;
    try {
      // JSON.parse sobre el texto pegado tal cual. No se corrigen
      // caracteres, campos ni valores automáticamente.
      parsed = JSON.parse(text);
    } catch (e) {
      setError('JSON no válido. Revisa la respuesta de BUILDUP.');
      onLoad(null);
      return;
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setError('El JSON debe ser un objeto (1 jugador · 1 partido).');
      onLoad(null);
      return;
    }
    setFileName(null);
    // Nota: mantenemos el texto pegado en el textarea para que el admin
    // pueda revisarlo; el RAW que se envía es el objeto parseado exacto.
    onLoad(parsed);
  };

  return (
    <Card data-testid="admin-pi-import-json-uploader">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileJson className="h-5 w-5" /> 2 · Aporta el JSON BUILDUP
        </CardTitle>
        <CardDescription>
          Sube un archivo `.json` o pega el JSON completo devuelto por
          BUILDUP. Formato NetScouters BUILDUP Player Match RAW v1.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload de archivo (existente) */}
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            data-testid="admin-pi-import-json-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            data-testid="admin-pi-import-json-select-btn"
            disabled={disabled}
          >
            <Upload className="h-4 w-4 mr-2" />
            {raw ? 'Reemplazar archivo' : 'Seleccionar archivo .json'}
          </Button>
          {fileName && (
            <span className="text-sm text-muted-foreground" data-testid="admin-pi-import-json-filename">
              {fileName}
            </span>
          )}
        </div>

        {/* Separador visual */}
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          o pega el JSON
          <span className="h-px flex-1 bg-border" />
        </div>

        {/* Pegado de JSON directo */}
        <div className="space-y-2">
          <Label htmlFor="admin-pi-import-json-paste" className="text-sm">
            Pega aquí la respuesta de BUILDUP
          </Label>
          <textarea
            id="admin-pi-import-json-paste"
            data-testid="admin-pi-import-json-paste"
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            disabled={disabled}
            spellCheck={false}
            rows={8}
            placeholder='{"match": { ... }, "player": { ... }, ... }'
            className="w-full rounded-md border bg-background p-3 font-mono text-xs leading-relaxed focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handlePasted}
              disabled={disabled || !pasted.trim()}
              data-testid="admin-pi-import-json-paste-btn"
            >
              Analizar JSON pegado
            </Button>
            {pasted && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setPasted(''); setError(null); }}
                data-testid="admin-pi-import-json-paste-clear"
              >
                Limpiar
              </Button>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="admin-pi-import-json-error">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Archivo o JSON inválido</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Preview mínimo (identidad + metadatos + 4 KPIs)
// ---------------------------------------------------------------------------
function MinimalPreview({ profile, raw, normalized, identity }) {
  const match = normalized?.match || {};
  const player = normalized?.player || {};
  const dq = normalized?.data_quality || null;
  const kpis = useMemo(
    () => (normalized ? selectKpisForPlayerMatch(normalized) : []),
    [normalized],
  );
  const rawPlayerName = _rawJsonPlayerName(raw) || '—';
  return (
    <Card data-testid="admin-pi-import-preview">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5" /> 3 · Preview mínimo
        </CardTitle>
        <CardDescription>
          Confirma que el archivo corresponde al jugador y partido correctos
          antes de guardar. Sin análisis completo, heatmap ni vídeo.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Identidad */}
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Jugador NetScouters seleccionado
            </div>
            <div className="font-medium" data-testid="admin-pi-import-preview-profile-name">
              {_profileFullName(profile)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {profile.club_name || '—'} · {profile.team_name || '—'} · {profile.position || 's/d'}
            </div>
          </div>
          <div className="rounded-lg border p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Jugador en el JSON BUILDUP
            </div>
            <div className="font-medium" data-testid="admin-pi-import-preview-json-name">
              {rawPlayerName}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {player.position || 's/d'} · {player.minutes_played != null ? `${player.minutes_played}′` : '—'}
            </div>
          </div>
        </div>

        {/* Discrepancia bloqueante */}
        {!identity.ok && (
          <Alert variant="destructive" data-testid="admin-pi-import-identity-mismatch">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Identidad no coincidente</AlertTitle>
            <AlertDescription>
              El nombre del JSON no coincide razonablemente con el jugador
              seleccionado. Revisa la selección; el importador no corrige
              esto automáticamente.
            </AlertDescription>
          </Alert>
        )}

        {/* Metadatos del partido */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetaBox label="Fecha" value={match.date || '—'} testId="admin-pi-import-preview-match-date" />
          <MetaBox label="Rival" value={match.opponent || '—'} testId="admin-pi-import-preview-match-opponent" />
          <MetaBox label="Equipo" value={match.team_name || match.team || '—'} testId="admin-pi-import-preview-match-team" />
          <MetaBox label="source_match_id"
            value={match.source_match_id || match.match_id || raw?.match?.match_id || raw?.match?.id || '—'}
            testId="admin-pi-import-preview-source-match-id" />
        </div>

        {/* Data quality + 4 headline KPIs */}
        <div className="flex items-center gap-2 pt-1">
          <span className="text-sm text-muted-foreground">Data quality:</span>
          {dq?.status ? (
            <Badge variant="outline" data-testid="admin-pi-import-preview-data-quality">
              {dq.status}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </div>
        {kpis?.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-pi-import-preview-kpis">
            {kpis.slice(0, 4).map((k) => (
              <div key={k.key} className="rounded-lg border p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{k.label}</div>
                <div className="text-xl font-semibold mt-0.5" data-testid={`admin-pi-import-preview-kpi-${k.key}`}>
                  {k.formatted != null ? k.formatted : '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetaBox({ label, value, testId }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-medium truncate" data-testid={testId}>{value ?? '—'}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------
export default function AdminPlayerIntelligenceImport() {
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [raw, setRaw] = useState(null);
  const [normalized, setNormalized] = useState(null);
  const [normalizeError, setNormalizeError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Normalizar cuando cambia el RAW.
  useEffect(() => {
    setNormalizeError(null);
    if (!raw) { setNormalized(null); return; }
    try {
      const nd = normalizePlayerMatch(raw, {
        // player_id/player_match_id no son la identidad canónica en 3B;
        // se ignoran en persistencia. Aquí sirven sólo para el preview.
        player_id: selectedProfile?.id,
      });
      if (!nd) {
        setNormalizeError('El normalizador no produjo salida (JSON incompleto).');
        setNormalized(null);
        return;
      }
      // Defensa en profundidad: nunca enviaremos `source_raw` al backend.
      const clean = { ...nd };
      delete clean.source_raw;
      setNormalized(clean);
    } catch (e) {
      setNormalizeError('Error normalizando el JSON. Revisa el archivo.');
      setNormalized(null);
    }
  }, [raw, selectedProfile?.id]);

  const identity = useMemo(
    () => (selectedProfile && raw ? _identityMatch(selectedProfile, raw) : { ok: false, reason: 'incomplete' }),
    [selectedProfile, raw],
  );

  const canSave = !!(selectedProfile && raw && normalized && identity.ok && !saving);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const { data, message } = await adminImportPlayerMatch({
        playerProfileId: selectedProfile.id,
        rawJson: raw,
        normalizedData: normalized,
      });
      toast.success(message || 'Análisis importado correctamente.');
      setResult(data);
    } catch (err) {
      toast.error(friendlyError(err) || 'No se pudo importar el análisis.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSelectedProfile(null);
    setRaw(null);
    setNormalized(null);
    setNormalizeError(null);
    setResult(null);
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6" data-testid="admin-pi-import-page">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="admin-pi-import-title">
            Player Intelligence · Importar análisis
          </h1>
          <p className="text-sm text-muted-foreground">
            Importa un nuevo análisis JSON BUILDUP para un jugador existente.
            Un JSON = 1 jugador + 1 partido.
          </p>
        </div>

        {result ? (
          <Card data-testid="admin-pi-import-success">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="h-5 w-5" /> Análisis importado correctamente.
              </CardTitle>
              <CardDescription>
                Guardado con id <span className="font-mono">{result.id}</span>.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button asChild data-testid="admin-pi-import-success-view">
                <Link to={`/jugadores/${encodeURIComponent(result.player_profile_id)}/analisis/${encodeURIComponent(result.id)}`}>
                  Ver análisis <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
              <Button variant="outline" onClick={handleReset} data-testid="admin-pi-import-success-again">
                Importar otro
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <PlayerPicker selected={selectedProfile} onSelect={setSelectedProfile} />

            <JsonUploader raw={raw} onLoad={setRaw} disabled={!selectedProfile} />

            {normalizeError && (
              <Alert variant="destructive" data-testid="admin-pi-import-normalize-error">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Normalización fallida</AlertTitle>
                <AlertDescription>{normalizeError}</AlertDescription>
              </Alert>
            )}

            {selectedProfile && raw && normalized && (
              <MinimalPreview
                profile={selectedProfile}
                raw={raw}
                normalized={normalized}
                identity={identity}
              />
            )}

            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={handleReset} data-testid="admin-pi-import-reset">
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave}
                data-testid="admin-pi-import-save"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar análisis
              </Button>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
